<template>
  <div class="absolute bottom-1 right-1">
    <div
      v-if="isLoading"
      class="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-700 bg-white p-1 opacity-50"
    >
      <div
        class="size-4 shrink-0 animate-spin rounded-full border-2 border-dotted border-gray-700 md:size-5"
        style="animation-duration: 4s"
      />
    </div>
    <div
      v-else-if="!validDestination"
      class="flex items-center justify-center rounded-full border border-gray-700 bg-white p-0.5 text-xs md:p-1 md:px-2 md:text-sm"
    >
      No disponible a tu dirección
    </div>
    <product-out-of-stock-button-on-image
      v-else-if="productOutOfStock"
      :product="product"
      @show-register-dialog="emit('show-register-dialog')"
    />
    <button
      v-else-if="!cartStore.itemPresent(product.id) || variantNeededAndNotPresent"
      class="flex size-7 items-center justify-center rounded-full border border-gray-700 bg-white p-1 transition-all duration-300 ease-in-out hover:rotate-180 hover:bg-gray-700 hover:text-white md:size-8"
      :disabled="variantNeededAndNotPresent"
      @click.prevent="handleInitialShopping"
    >
      <PlusIcon class="size-4 md:size-5" />
    </button>
    <template v-else-if="quantity > 0">
      <div
        v-if="isMobile"
        class="group relative flex size-7 items-center justify-center overflow-hidden rounded-full bg-gray-700 p-1 text-white transition-all duration-300 ease-in-out md:size-8"
        :class="{ 'w-24 md:w-24': showControls }"
      >
        <button
          v-if="!showControls"
          class="flex size-5 items-center justify-center p-1 text-sm transition-opacity duration-300 ease-in-out md:text-base"
          type="button"
          @click.prevent="showQuantityControls"
        >
          {{ quantity }}
        </button>
        <div
          v-if="showControls"
          class="absolute inset-0 flex items-center justify-center transition-all duration-300 ease-in-out"
        >
          <button
            type="button"
            class="rounded-bl-md px-2 text-white"
            :class="{ 'rounded-l-md md:p-2': inProductOrOrdersPath, 'opacity-30': cartProductMutationIsLoading }"
            :disabled="quantity === 0 || cartProductMutationIsLoading"
            @click.prevent="removeOneUnit"
          >
            <MinusIcon class="size-4" />
          </button>
          <Field
            v-model.number="quantityField"
            :disabled="product.packUnits !== 1"
            :label="product.name"
            :name="`quantity-${product.id}`"
            class="w-8 bg-gray-700 text-center text-sm text-white focus:outline-none md:text-base"
            :rules="{ asyncStockValidation: product.id }"
            @click.prevent
            @change="manageQuantityChange"
          />
          <button
            type="button"
            class="rounded-br-md px-2 text-white"
            :class="{ 'rounded-r-md md:p-2': inProductOrOrdersPath, 'opacity-30': cartProductMutationIsLoading }"
            :disabled="increaseButtonDisabled || cartProductMutationIsLoading"
            @click.prevent="increaseOneUnit"
          >
            <PlusIcon class="size-4" />
          </button>
        </div>
      </div>
      <div
        v-else
        class="group relative flex size-7 items-center justify-center overflow-hidden rounded-full bg-gray-700 p-1 text-white transition-all duration-300 ease-in-out hover:sm:w-24 md:size-8"
      >
        <button
          class="flex size-5 items-center justify-center p-1 text-sm transition-opacity duration-300 ease-in-out group-hover:opacity-0 md:text-base"
          type="button"
          @click.prevent="showQuantityControls"
        >
          {{ quantity }}
        </button>
        <div
          class="absolute inset-0 flex items-center justify-center transition-all duration-300 ease-in-out group-hover:opacity-100"
          :class="{ 'opacity-0': !showControls, 'opacity-100': showControls }"
        >
          <button
            type="button"
            class="rounded-bl-md px-2 text-white"
            :class="{ 'rounded-l-md md:p-2': inProductOrOrdersPath, 'opacity-30': cartProductMutationIsLoading }"
            :disabled="quantity === 0 || cartProductMutationIsLoading"
            @click.prevent="removeOneUnit"
          >
            <MinusIcon class="size-4" />
          </button>
          <Field
            v-model.number="quantityField"
            :disabled="product.packUnits !== 1"
            :label="product.name"
            :name="`quantity-${product.id}`"
            class="w-8 bg-gray-700 text-center text-sm text-white focus:outline-none md:text-base"
            :rules="{ asyncStockValidation: product.id }"
            @click.prevent
            @change="manageQuantityChange"
          />
          <button
            type="button"
            class="rounded-br-md px-2 text-white"
            :class="{ 'rounded-r-md md:p-2': inProductOrOrdersPath, 'opacity-30': cartProductMutationIsLoading }"
            :disabled="increaseButtonDisabled || cartProductMutationIsLoading"
            @click.prevent="increaseOneUnit"
          >
            <PlusIcon class="size-4" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { MinusIcon, PlusIcon } from '@heroicons/vue/24/solid';
import { computed, ref, watch, inject, type Ref } from 'vue';
import { Field } from 'vee-validate';
import useCartStore from 'stores/cart';
import useSessionStore from 'stores/user-store';
import useCartManager from './use/cart-manager';
import useValidDestinationQuery from './queries/valid-destination-query';
import useMakerQuery from './queries/maker-query';
import ProductOutOfStockButtonOnImage from './product-out-of-stock-button-on-image.vue';

interface ProductShoppingButtonProps {
  product: Product;
  variantNeeded?: boolean;
  variant?: Variant;
}

const props = withDefaults(defineProps<ProductShoppingButtonProps>(), {
  variantNeeded: false,
  variant: undefined,
});

const productOutOfStock = computed(
  () =>
    props.product.status === 'out_of_stock' ||
    (props.product.inventory?.enable && props.product.inventory?.quantity === 0),
);

const computedProduct = computed(() => props.product);
const {
  increaseOneUnit,
  removeOneUnit,
  quantity,
  quantityField,
  manageQuantityChange,
  increaseButtonDisabled,
  cartProductMutationIsLoading,
} = useCartManager(computedProduct, props.variant);

const cartStore = useCartStore();
const showControls = ref(false);
const hideControlsTimeout = ref<NodeJS.Timeout | null>(null);
const isMobile: Ref<boolean> = inject('isMobile') as Ref<boolean>;

const emit = defineEmits<{ 'show-register-dialog': [] }>();

function clearHideControlsTimeout() {
  showControls.value = true;
  if (hideControlsTimeout.value) clearTimeout(hideControlsTimeout.value);
  hideControlsTimeout.value = null;
}

const showTime = computed(() => (isMobile.value ? 1500 : 10));
function setHideControlsTimeout() {
  clearHideControlsTimeout();
  hideControlsTimeout.value = setTimeout(() => {
    showControls.value = false;
    hideControlsTimeout.value = null;
  }, showTime.value);
}

watch(
  () => quantity.value,
  (newQuantity) => {
    if (newQuantity > 0) {
      showControls.value = true;
      setHideControlsTimeout();
    }
  },
  { immediate: true },
);

function showQuantityControls() {
  showControls.value = true;
  setHideControlsTimeout();
}

const sessionStore = useSessionStore();
function handleInitialShopping() {
  if (sessionStore.user?.id) {
    increaseOneUnit();
  } else {
    emit('show-register-dialog');
  }
}

const variantNeededAndNotPresent = computed(() => props.variantNeeded && !props.variant?.id);
const currentPath = computed(() => window.location.pathname);
const inProductOrOrdersPath = computed(() => {
  const regex = /\/products\/[\d]+|\/orders\/(new|[\d]+)/;

  return regex.test(currentPath.value);
});

const { maker, makerQuery } = useMakerQuery(computed(() => props.product.makerId));
const currentCommuneId = computed(() => sessionStore.currentAddress.communeId);
const makerDestinationQueryEnabled = computed(() => !!maker.value?.id && maker.value.destinationConstrainsCount > 0);
const computedMakerId = computed(() => props.product.makerId);
const { validDestination: validMakerDestination, validDestinationIsLoading: validMakerDestinationIsLoading } =
  useValidDestinationQuery(
    computedMakerId,
    computed(() => 'Maker'),
    currentCommuneId,
    makerDestinationQueryEnabled,
  );
const computedProductId = computed(() => props.product.id);
const productDestinationQueryEnabled = computed(() => props.product.destinationConstrainsCount > 0);
const { validDestination: validProductDestination, validDestinationIsLoading: validProductDestinationIsLoading } =
  useValidDestinationQuery(
    computedProductId,
    computed(() => 'Product'),
    currentCommuneId,
    productDestinationQueryEnabled,
  );
const validDestination = computed(() => {
  if (validProductDestination.value !== undefined) return validProductDestination.value;
  else if (validMakerDestination.value !== undefined) return validMakerDestination.value;

  return true;
});

const isLoading = computed(
  () => makerQuery.isLoading.value || validMakerDestinationIsLoading.value || validProductDestinationIsLoading.value,
);
</script>
